// src/game/Game.js
// 🎮 Main Wedding Game Class - UPDATED: Full screen scaling with mobile controls
// Clean, simple game initialization with retro feel and responsive design

import Phaser from 'phaser';
import { GAME_CONFIG, GAME_STATES } from './config.js';
import { gameState } from './state.js';

// Import scenes
import MenuScene from '../scenes/MenuScene.js';
import GameScene from '../scenes/GameScene.js';
import LevelCompleteScene from '../scenes/LevelCompleteScene.js';
import ScoreboardScene from '../scenes/ScoreboardScene.js';

export class WeddingGame {
  constructor() {
    console.log('🎮 Initializing 90s Wedding Game...');
    
    this.config = this.createGameConfig();
    this.game = null;
    this.isInitialized = false;
    
    this.init();
  }

  createGameConfig() {
    const config = {
      type: Phaser.AUTO,
      width: GAME_CONFIG.CANVAS.WIDTH,
      height: GAME_CONFIG.CANVAS.HEIGHT,
      parent: 'game-container',
      backgroundColor: GAME_CONFIG.CANVAS.BACKGROUND_COLOR,
      
      // 90s Pixel Art Rendering
      render: {
        pixelArt: GAME_CONFIG.CANVAS.PIXEL_ART,
        antialias: GAME_CONFIG.CANVAS.ANTIALIAS,
        roundPixels: true,
        powerPreference: 'low-power'
      },
      
      // UPDATED: Enhanced landscape-first responsive scaling
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: GAME_CONFIG.CANVAS.WIDTH,
        height: GAME_CONFIG.CANVAS.HEIGHT,
        min: {
          width: 480,   // Better minimum for landscape
          height: 320   // Better minimum for landscape
        },
        max: {
          width: 1920,
          height: 1080
        },
        fullscreenTarget: 'game-container',
        expandParent: true,
        // LANDSCAPE OPTIMIZED scaling
        orientation: Phaser.Scale.LANDSCAPE // Prefer landscape
      },
      
      // Rest of config unchanged...
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: GAME_CONFIG.PHYSICS.GRAVITY },
          debug: GAME_CONFIG.DEBUG.SHOW_PHYSICS,
          fps: GAME_CONFIG.PERFORMANCE.TARGET_FPS,
          fixedStep: false
        }
      },
      
      audio: {
        disableWebAudio: false,
        context: false,
        noAudio: false
      },
      
      input: {
        keyboard: true,
        mouse: true,
        touch: true,
        activePointers: 5,
        smoothFactor: 0.2,
        targetFPS: 60
      },
      
      scene: [
        MenuScene,
        GameScene, 
        LevelCompleteScene,
        ScoreboardScene
      ],
      
      callbacks: {
        preBoot: (game) => {
          console.log('🎮 Game pre-boot...');
        },
        postBoot: (game) => {
          console.log('✅ Game booted successfully!');
          this.onGameReady();
        }
      }
    };

    return config;
  }

  init() {
    try {
      console.log('🎮 Creating Phaser game instance...');
      this.game = new Phaser.Game(this.config);
      
      // Setup global event listeners
      this.setupEventListeners();
      
      // Setup error handling
      this.setupErrorHandling();
      
      this.isInitialized = true;
      console.log('✅ Wedding Game initialized successfully!');
      
    } catch (error) {
      console.error('❌ Failed to initialize Wedding Game:', error);
      this.showErrorMessage(error);
    }
  }

  onGameReady() {
    // Hide loading screen
    this.hideLoadingScreen();
    
    // Initialize game state
    gameState.setState(GAME_STATES.MENU);
    
    // Setup mobile-specific features
    this.setupMobileFeatures();
    
    // Setup performance monitoring
    if (GAME_CONFIG.DEBUG.SHOW_FPS) {
      this.setupFPSMonitor();
    }
    
    // Setup full screen scaling
    this.setupFullScreenScaling();
    
    console.log('🎉 Wedding Game ready to play!');
  }

  setupFullScreenScaling() {
    // Make game container fill the entire viewport with dynamic viewport units
    const gameContainer = document.getElementById('game-container');
    if (gameContainer) {
      gameContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100dvh; /* Dynamic viewport height - excludes mobile browser UI */
        margin: 0;
        padding: 0;
        overflow: hidden;
        z-index: 1;
      `;
    }
    
    // Ensure body and html use dynamic viewport units
    document.body.style.cssText = `
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100dvh; /* Dynamic viewport height */
      overflow: hidden;
      background: #000;
      touch-action: none;
      user-select: none;
      -webkit-user-select: none;
      overscroll-behavior: none; /* Prevent bounce scrolling */
    `;
    
    document.documentElement.style.cssText = `
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100dvh; /* Dynamic viewport height */
      overflow: hidden;
    `;
    
    // Enhanced mobile setup
    this.setupMobileViewport();
    
    // Handle orientation changes
    this.handleOrientationChange();
    
    console.log('📱 Full screen scaling setup with dynamic viewport units');
  }

  setupMobileViewport() {
    // Enhanced viewport meta for better mobile experience
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      document.head.appendChild(viewport);
    }
    viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, shrink-to-fit=no, viewport-fit=cover';
    
    // Add mobile-optimized CSS with dismissible portrait notice
    const mobileCSS = document.createElement('style');
    mobileCSS.textContent = `
      /* Support for all viewport units */
      :root {
        --vh: 1vh;
        --dvh: 1dvh;
        --svh: 1svh;
        --lvh: 1lvh;
      }
      
      /* Mobile viewport handling */
      @supports (height: 100dvh) {
        #game-container {
          height: 100dvh !important;
        }
        body, html {
          height: 100dvh !important;
        }
      }
      
      /* Fallback for older browsers */
      @supports not (height: 100dvh) {
        #game-container {
          height: 100vh !important;
          height: calc(var(--vh, 1vh) * 100) !important;
        }
      }
      
      /* Dismissible portrait notice for small screens */
      @media screen and (orientation: portrait) and (max-width: 768px) and (max-height: 480px) {
        #portrait-notice {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0,0,0,0.95);
          color: white;
          padding: 20px;
          border-radius: 10px;
          font-family: 'Press Start 2P', monospace;
          font-size: 10px;
          text-align: center;
          z-index: 10000;
          max-width: 280px;
          line-height: 1.4;
          border: 2px solid #FFD700;
        }
        
        #portrait-notice button {
          margin-top: 15px;
          padding: 10px 20px;
          background: #32CD32;
          color: white;
          border: none;
          font-family: 'Press Start 2P', monospace;
          font-size: 8px;
          cursor: pointer;
          border-radius: 5px;
        }
        
        #portrait-notice button:hover {
          background: #228B22;
        }
      }
      
      /* Add to Home Screen notice */
      #home-screen-notice {
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(255, 215, 0, 0.95);
        color: #000;
        padding: 15px;
        border-radius: 10px;
        font-family: 'Press Start 2P', monospace;
        font-size: 8px;
        text-align: center;
        z-index: 9999;
        max-width: 280px;
        border: 2px solid #FFA500;
        animation: slideUp 0.5s ease-out;
      }
      
      #home-screen-notice button {
        margin: 10px 5px 0 5px;
        padding: 8px 15px;
        background: #FF69B4;
        color: white;
        border: none;
        font-family: 'Press Start 2P', monospace;
        font-size: 6px;
        cursor: pointer;
        border-radius: 5px;
      }
      
      #home-screen-notice .dismiss {
        background: #808080;
      }
      
      @keyframes slideUp {
        from { transform: translateX(-50%) translateY(100px); opacity: 0; }
        to { transform: translateX(-50%) translateY(0); opacity: 1; }
      }
      
      /* Enhanced mobile controls - bigger buttons */
      @media (max-width: 768px) {
        .mobile-control-button {
          min-width: 80px !important;
          min-height: 80px !important;
        }
      }
    `;
    document.head.appendChild(mobileCSS);
    
    // Set up dynamic viewport height calculation for older browsers
    this.setupDynamicViewportHeight();
    
    // Show portrait notice if needed
    this.showPortraitNotice();
    
    // Show add to home screen notice
    this.showAddToHomeScreenNotice();
  }

  showAddToHomeScreenNotice() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    const isInWebApp = window.navigator.standalone === true;
    
    // Only show if mobile, not already in standalone mode, and not dismissed recently
    if (isMobile && !isStandalone && !isInWebApp) {
      const dismissed = localStorage.getItem('home-screen-notice-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const now = Date.now();
      
      // Show notice if not dismissed in last 24 hours
      if (now - dismissedTime > 86400000) {
        // Show after 3 seconds to not overwhelm user
        setTimeout(() => {
          const notice = document.createElement('div');
          notice.id = 'home-screen-notice';
          
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const instructions = isIOS ? 
            'Tap Share → Add to Home Screen' : 
            'Tap Menu → Add to Home Screen';
          
          notice.innerHTML = `
            <div>🏠 Install Wedding Game for best experience!</div>
            <div style="font-size: 6px; margin-top: 8px;">${instructions}</div>
            <button id="install-pwa-btn">
              📱 INSTALL
            </button>
            <button class="dismiss" onclick="localStorage.setItem('home-screen-notice-dismissed', Date.now()); this.parentElement.remove();">
              LATER
            </button>
          `;
          document.body.appendChild(notice);
          
          // FIXED: Add proper install functionality
          document.getElementById('install-pwa-btn').addEventListener('click', async () => {
            const installed = await this.triggerPWAInstall();
            if (installed) {
              notice.remove();
            } else {
              // Fallback to manual instructions
              notice.innerHTML = `
                <div style="font-size: 10px;">
                  <div>📱 To install manually:</div>
                  <div style="margin: 10px 0; font-size: 8px;">${instructions}</div>
                  <button onclick="localStorage.setItem('home-screen-notice-dismissed', Date.now()); this.parentElement.remove();">
                    OK
                  </button>
                </div>
              `;
            }
          });
          
          // Auto-remove after 15 seconds
          setTimeout(() => {
            if (notice.parentElement) {
              notice.remove();
            }
          }, 15000);
        }, 3000);
      }
    }
  }


  showPortraitNotice() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isPortrait = window.innerHeight > window.innerWidth;
    
    // FIXED: Show notice for mobile portrait mode (removed small screen restriction)
    if (isMobile && isPortrait) {
      // Check if notice was dismissed recently
      const dismissed = localStorage.getItem('portrait-notice-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const now = Date.now();
      
      // Show notice if not dismissed in last hour
      if (now - dismissedTime > 3600000) {
        const notice = document.createElement('div');
        notice.id = 'portrait-notice';
        notice.innerHTML = `
          <div>📱 Rotate to landscape for optimal gaming experience! 🎮</div>
          <div style="font-size: 8px; margin-top: 10px; opacity: 0.8;">
            (You can still play in portrait mode)
          </div>
          <button onclick="this.parentElement.remove(); localStorage.setItem('portrait-notice-dismissed', Date.now())">
            GOT IT!
          </button>
        `;
        document.body.appendChild(notice);
        
        // Auto-remove after 8 seconds
        setTimeout(() => {
          if (notice.parentElement) {
            notice.remove();
          }
        }, 8000);
      }
    }
  }


  setupDynamicViewportHeight() {
    // Calculate dynamic viewport height for older browsers
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', () => {
      setTimeout(setViewportHeight, 100);
    });
  }

  handleOrientationChange() {
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        if (this.game && this.game.scale) {
          this.game.scale.refresh();
          console.log('🔄 Screen orientation changed, scaling refreshed');
        }
        
        // Update portrait notice if needed
        this.showPortraitNotice();
      }, 100);
    });
    
    // Listen for resize events
    window.addEventListener('resize', () => {
      if (this.game && this.game.scale) {
        this.game.scale.refresh();
      }
    });
  }

  setupEventListeners() {
    // Window events
    window.addEventListener('resize', () => {
      this.handleResize();
    });
    
    window.addEventListener('orientationchange', () => {
      setTimeout(() => this.handleResize(), 500);
    });
    
    // Visibility change (mobile tab switching)
    document.addEventListener('visibilitychange', () => {
      this.handleVisibilityChange();
    });
    
    // Prevent context menu on mobile
    document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
    
    // Prevent zoom on mobile
    document.addEventListener('touchstart', (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    });
    
    // Prevent scroll on mobile
    document.addEventListener('touchmove', (e) => {
      e.preventDefault();
    }, { passive: false });
    
    // Handle back button on mobile
    window.addEventListener('beforeunload', (e) => {
      this.handleGameExit();
    });
  }

  setupErrorHandling() {
    // Phaser error handling
    this.game.events.on('error', (error) => {
      console.error('🎮 Phaser error:', error);
      this.showErrorMessage(error);
    });
    
    // Global error handling
    window.addEventListener('error', (event) => {
      console.error('🌐 Global error:', event.error);
      this.showErrorMessage(event.error);
    });
    
    // Promise rejection handling
    window.addEventListener('unhandledrejection', (event) => {
      console.error('🌐 Unhandled promise rejection:', event.reason);
      this.showErrorMessage(event.reason);
    });
  }

  setupMobileFeatures() {
    // Prevent mobile scroll
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    
    // Setup haptic feedback if available
    if ('vibrate' in navigator && GAME_CONFIG.MOBILE.VIBRATION) {
      this.hapticFeedback = {
        light: () => navigator.vibrate(50),
        medium: () => navigator.vibrate(100),
        heavy: () => navigator.vibrate(200)
      };
    } else {
      this.hapticFeedback = {
        light: () => {},
        medium: () => {},
        heavy: () => {}
      };
    }
    
    // Setup mobile-specific UI adjustments
    this.adjustForMobile();
    
    // ADDED: Setup PWA install prompt
    this.setupPWAInstall();
  }

  setupPWAInstall() {
    let deferredPrompt = null;
    
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e;
      console.log('📱 PWA install prompt available');
    });
    
    // Store the prompt for later use
    this.installPrompt = deferredPrompt;
    
    // Handle install success
    window.addEventListener('appinstalled', () => {
      console.log('🎉 PWA installed successfully');
      this.installPrompt = null;
      // Hide any install notices
      const notice = document.getElementById('home-screen-notice');
      if (notice) notice.remove();
    });
  }

  // NEW: Trigger PWA install
  async triggerPWAInstall() {
    if (this.installPrompt) {
      try {
        const result = await this.installPrompt.prompt();
        console.log('📱 PWA install result:', result.outcome);
        this.installPrompt = null;
        return result.outcome === 'accepted';
      } catch (error) {
        console.warn('PWA install failed:', error);
        return false;
      }
    }
    return false;
  }

  adjustForMobile() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Adjust canvas for mobile
      const canvas = this.game.canvas;
      canvas.style.touchAction = 'none';
      canvas.style.userSelect = 'none';
      
      // Hide address bar on mobile
      setTimeout(() => {
        window.scrollTo(0, 1);
      }, 100);
      
      // Add viewport meta tag if it doesn't exist
      let viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.name = 'viewport';
        document.head.appendChild(viewport);
      }
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, shrink-to-fit=no';
      
      console.log('📱 Mobile optimizations applied');
    }
  }

  setupFPSMonitor() {
    // Simple FPS display
    const fpsText = document.createElement('div');
    fpsText.style.cssText = `
      position: fixed;
      top: 10px;
      left: 10px;
      color: white;
      font-family: 'Press Start 2P', monospace;
      font-size: 8px;
      z-index: 9999;
      background: rgba(0,0,0,0.5);
      padding: 4px;
    `;
    document.body.appendChild(fpsText);
    
    let lastTime = performance.now();
    let frames = 0;
    
    const updateFPS = () => {
      frames++;
      const currentTime = performance.now();
      
      if (currentTime - lastTime >= 1000) {
        fpsText.textContent = `FPS: ${frames}`;
        frames = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(updateFPS);
    };
    
    updateFPS();
  }

  // === EVENT HANDLERS ===

  handleResize() {
    if (this.game && this.game.scale) {
      this.game.scale.refresh();
      console.log(`📱 Game resized to ${window.innerWidth}x${window.innerHeight}`);
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      // Game lost focus - pause if playing
      if (gameState.currentState === GAME_STATES.PLAYING) {
        this.pauseGame();
      }
      
      // Mute audio
      if (this.game && this.game.sound) {
        this.game.sound.mute = true;
      }
    } else {
      // Game gained focus - resume
      if (this.game && this.game.sound) {
        this.game.sound.mute = false;
      }
    }
  }

  handleGameExit() {
    // Save game state if needed
    this.saveGameState();
  }

  // === GAME CONTROL METHODS ===

  startGame(playerName, character) {
    gameState.setPlayer(playerName, character);
    gameState.setState(GAME_STATES.PLAYING);
    gameState.startLevel(1);
    
    // Start game scene
    this.game.scene.start('GameScene');
    
    console.log(`🚀 Starting game: ${playerName} as ${character}`);
  }

  pauseGame() {
    if (gameState.currentState === GAME_STATES.PLAYING) {
      gameState.setState(GAME_STATES.PAUSED);
      this.game.scene.pause('GameScene');
      this.showPauseOverlay();
    }
  }

  resumeGame() {
    if (gameState.currentState === GAME_STATES.PAUSED) {
      gameState.setState(GAME_STATES.PLAYING);
      this.game.scene.resume('GameScene');
      this.hidePauseOverlay();
    }
  }

  restartGame() {
    gameState.reset();
    gameState.setState(GAME_STATES.MENU);
    this.game.scene.start('MenuScene');
  }

  // === UI OVERLAY METHODS ===

  showPauseOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'pause-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      font-family: 'Press Start 2P', monospace;
      color: white;
    `;
    
    overlay.innerHTML = `
      <div style="text-align: center;">
        <h2 style="font-size: 16px; margin-bottom: 20px; color: #FFD700;">⏸️ GAME PAUSED</h2>
        <p style="font-size: 10px; margin-bottom: 30px;">Tap anywhere to continue</p>
        <div style="display: flex; gap: 20px; justify-content: center;">
          <button id="resume-btn" style="
            font-family: 'Press Start 2P', monospace;
            font-size: 8px;
            padding: 10px 20px;
            background: #32CD32;
            color: white;
            border: 2px solid #228B22;
            cursor: pointer;
          ">RESUME</button>
          <button id="menu-btn" style="
            font-family: 'Press Start 2P', monospace;
            font-size: 8px;
            padding: 10px 20px;
            background: #FF6347;
            color: white;
            border: 2px solid #DC143C;
            cursor: pointer;
          ">MENU</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(overlay);
    
    // Event handlers
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.resumeGame();
    });
    
    document.getElementById('resume-btn').addEventListener('click', () => {
      this.resumeGame();
    });
    
    document.getElementById('menu-btn').addEventListener('click', () => {
      this.hidePauseOverlay();
      this.restartGame();
    });
  }

  hidePauseOverlay() {
    const overlay = document.getElementById('pause-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  showErrorMessage(error) {
    const errorOverlay = document.createElement('div');
    errorOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(45deg, #FF69B4, #4ECDC4);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      font-family: 'Press Start 2P', monospace;
      color: white;
      text-align: center;
      padding: 20px;
    `;
    
    errorOverlay.innerHTML = `
      <h1 style="font-size: 20px; margin-bottom: 20px;">💔 OOPS!</h1>
      <p style="font-size: 10px; margin-bottom: 20px; max-width: 400px; line-height: 1.5;">
        The wedding game hit a small bump! Even the best weddings have little hiccups.
      </p>
      <div style="
        background: rgba(0,0,0,0.5);
        padding: 10px;
        margin-bottom: 20px;
        font-size: 8px;
        max-width: 500px;
        word-break: break-word;
      ">
        ${error.message || error}
      </div>
      <button onclick="location.reload()" style="
        font-family: 'Press Start 2P', monospace;
        font-size: 10px;
        padding: 15px 30px;
        background: #FFD700;
        color: #2C3E50;
        border: 3px solid #FFA500;
        cursor: pointer;
      ">🔄 RESTART GAME</button>
    `;
    
    document.body.appendChild(errorOverlay);
  }

  hideLoadingScreen() {
    const loading = document.getElementById('loading');
    if (loading) {
      loading.classList.add('fade-out');
      setTimeout(() => {
        if (loading.parentNode) {
          loading.parentNode.removeChild(loading);
        }
      }, 500);
    }
  }

  // === GAME STATE MANAGEMENT ===

  saveGameState() {
    try {
      const saveData = gameState.exportState();
      localStorage.setItem('wedding-game-save', JSON.stringify(saveData));
      console.log('💾 Game state saved');
    } catch (error) {
      console.warn('⚠️ Could not save game state:', error);
    }
  }

  loadGameState() {
    try {
      const saveData = localStorage.getItem('wedding-game-save');
      if (saveData) {
        gameState.importState(JSON.parse(saveData));
        console.log('📂 Game state loaded');
        return true;
      }
    } catch (error) {
      console.warn('⚠️ Could not load game state:', error);
    }
    return false;
  }

  clearSaveData() {
    try {
      localStorage.removeItem('wedding-game-save');
      console.log('🗑️ Save data cleared');
    } catch (error) {
      console.warn('⚠️ Could not clear save data:', error);
    }
  }

  // === API INTEGRATION ===

  async saveScore(scoreData) {
    try {
      const response = await fetch(`${GAME_CONFIG.API.BASE_URL}${GAME_CONFIG.API.ENDPOINTS.SCORES}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scoreData),
        signal: AbortSignal.timeout(GAME_CONFIG.API.TIMEOUT)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('💾 Score saved to server:', result);
      return result;
      
    } catch (error) {
      console.warn('⚠️ Could not save score to server:', error);
      
      // Fallback: save locally
      this.saveScoreLocally(scoreData);
      return null;
    }
  }

  saveScoreLocally(scoreData) {
    try {
      const localScores = JSON.parse(localStorage.getItem('wedding-game-scores') || '[]');
      
      localScores.push({
        ...scoreData,
        timestamp: new Date().toISOString(),
        offline: true
      });
      
      // Keep only top 10 scores
      localScores.sort((a, b) => b.score - a.score);
      localScores.splice(10);
      
      localStorage.setItem('wedding-game-scores', JSON.stringify(localScores));
      console.log('💾 Score saved locally');
      
    } catch (error) {
      console.warn('⚠️ Could not save score locally:', error);
    }
  }

  async loadLeaderboard() {
    try {
      const response = await fetch(`${GAME_CONFIG.API.BASE_URL}${GAME_CONFIG.API.ENDPOINTS.SCORES}`, {
        signal: AbortSignal.timeout(GAME_CONFIG.API.TIMEOUT)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const scores = await response.json();
      console.log('📊 Leaderboard loaded from server');
      return scores;
      
    } catch (error) {
      console.warn('⚠️ Could not load leaderboard from server:', error);
      
      // Fallback: load local scores
      return this.loadLocalScores();
    }
  }

  loadLocalScores() {
    try {
      const localScores = JSON.parse(localStorage.getItem('wedding-game-scores') || '[]');
      console.log('📊 Loaded local scores');
      return localScores;
    } catch (error) {
      console.warn('⚠️ Could not load local scores:', error);
      return [];
    }
  }

  // === UTILITY METHODS ===

  vibrate(type = 'light') {
    if (this.hapticFeedback) {
      this.hapticFeedback[type]();
    }
  }

  playSound(key, volume = 1) {
    if (this.game && this.game.sound) {
      this.game.sound.play(key, { volume: volume * GAME_CONFIG.AUDIO.SFX_VOLUME });
    }
  }

  getGameInfo() {
    return {
      title: GAME_CONFIG.TITLE,
      version: GAME_CONFIG.VERSION,
      playerName: gameState.playerName,
      character: gameState.character,
      level: gameState.level,
      score: gameState.score,
      state: gameState.currentState,
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight,
        orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
      }
    };
  }

  // === DEVELOPMENT HELPERS ===

  skipToLevel(levelNumber) {
    if (GAME_CONFIG.DEBUG.SKIP_INTRO) {
      gameState.level = levelNumber;
      gameState.setState(GAME_STATES.PLAYING);
      this.game.scene.start('GameScene');
    }
  }

  addDebugCommands() {
    if (GAME_CONFIG.DEBUG.CONSOLE_LOGGING) {
      // Add global debug commands
      window.weddingGame = this;
      window.gameState = gameState;
      
      // Debug shortcuts
      window.skipLevel = () => this.skipToLevel(gameState.level + 1);
      window.addScore = (points) => gameState.collectItem(points);
      window.showAchievements = () => console.log(gameState.achievements);
      
      console.log('🛠️ Debug commands available: weddingGame, gameState, skipLevel(), addScore(points), showAchievements()');
    }
  }

  // === CLEANUP ===

  destroy() {
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
    }
    
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Clear overlays
    this.hidePauseOverlay();
    
    console.log('🗑️ Wedding Game destroyed');
  }
}

// Create and export game instance
let gameInstance = null;

export function createGame() {
  if (gameInstance) {
    gameInstance.destroy();
  }
  
  gameInstance = new WeddingGame();
  
  // Add debug commands in development
  if (GAME_CONFIG.DEBUG.CONSOLE_LOGGING) {
    gameInstance.addDebugCommands();
  }
  
  return gameInstance;
}

export function getGame() {
  return gameInstance;
}

export default WeddingGame;